import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { format, addWeeks, startOfWeek, isSameMonth } from "date-fns";
import { sv } from "date-fns/locale";
import { X, ChevronLeft, ChevronRight, Loader2, ListOrdered, ArrowRightLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import WeekCalendar from "@/components/calendar/WeekCalendar";
import MiniCalendarWidget from "@/components/calendar/MiniCalendarWidget";
import CalendarSyncSection from "@/components/calendar/CalendarSyncSection";
import PaymentStatusBadge from "@/components/booking/PaymentStatusBadge";
import BookingDetailModal from "@/components/booking/BookingDetailModal";
import BookingHistoryTable from "@/components/booking/BookingHistoryTable";
import RescheduleDialog from "@/components/booking/RescheduleDialog";
import { BookingCard } from "@/components/booking/BookingCard";
import CancelBookingDialog from "@/components/booking/CancelBookingDialog";
import ResponsiveLayout from "@/components/layout/ResponsiveLayout";
import { useMyBookings } from "@/hooks/useMyBookings";
import type { BookingWithCourt, BookingPaymentRow, WaitlistRow } from "@/hooks/useMyBookings";
import { isCustomerRescheduleWindow, canCustomerReschedule } from "@/lib/customerReschedule";
import { bookingDisplayAmountMinor } from "@/lib/bookingDisplayAmount";

function getWeekStart(d: Date) {
  return startOfWeek(d, { weekStartsOn: 1 });
}

export default function Bookings() {
  const { t } = useTranslation('booking');
  const {
    user,
    hallId,
    hallHours,
    bookings,
    isLoading,
    payments,
    upcoming,
    past,
    cancelMutation,
    canCancel,
    handlePayNow,
    payingBookingId,
    checkinMutation,
    getCheckinState,
    selectedBookingId,
    setSelectedBookingId,
    rescheduleBooking,
    setRescheduleBooking,
    myWaitlist,
    leaveWaitlistMutation,
    weekStart,
    setWeekStart,
    calendarEvents,
    calLoading,
  } = useMyBookings();

  const [cancelTarget, setCancelTarget] = useState<BookingWithCourt | null>(null);

  const isCancelTargetPaid = cancelTarget
    ? (payments as BookingPaymentRow[]).some(
        (p) => p.booking_id === cancelTarget.id && ["paid", "manual"].includes(p.status),
      )
    : false;

  const bookingCardProps = {
    allBookings: bookings as BookingWithCourt[] | undefined,
    payments: payments as BookingPaymentRow[],
    userId: user?.id,
    payingBookingId,
    cancelIsPending: cancelMutation.isPending,
    checkinIsPending: checkinMutation.isPending,
    canCancel,
    getCheckinState,
    onSelect: (id: string) => setSelectedBookingId(id),
    onCheckin: (id: string) => checkinMutation.mutate(id),
    onPayNow: handlePayNow,
    onReschedule: (b: BookingWithCourt) => setRescheduleBooking(b),
    onCancel: (id: string) => {
      const b = (bookings as BookingWithCourt[])?.find((bk) => bk.id === id);
      if (b) setCancelTarget(b);
    },
  };

  // Stats for this month
  const monthStats = useMemo(() => {
    const now = new Date();
    const allBookings = (bookings as BookingWithCourt[] | undefined) ?? [];
    const thisMonth = allBookings.filter((b) =>
      isSameMonth(new Date(b.start_time), now) && b.status === "confirmed"
    );
    // Samma helper som tabellen och korten — annars stod "0 kr denna månad"
    // bredvid rader som visar riktiga belopp. Gruppsyskon ger 0 här, så en
    // gruppbokning räknas exakt en gång.
    const totalMinor = thisMonth.reduce(
      (sum, b) =>
        sum + bookingDisplayAmountMinor(b, payments as BookingPaymentRow[], user?.id, allBookings),
      0,
    );
    return { count: thisMonth.length, amount: Math.round(totalMinor / 100) };
  }, [bookings, payments, user?.id]);

  // Waitlist section (shared between mobile and desktop)
  const waitlistSection = myWaitlist.length > 0 && (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold flex items-center gap-1.5 text-muted-foreground">
        <ListOrdered className="h-4 w-4" /> {t('waitlist')} ({myWaitlist.length})
      </h3>
      {myWaitlist.map((w: WaitlistRow) => (
        <Card key={w.id} className={w.status === "offered" ? "border-primary bg-primary/5" : ""}>
          <CardContent className="p-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">
                {w.courts?.name ?? "Bana"} · {format(new Date(w.date + "T00:00:00"), "d MMM", { locale: sv })}
              </p>
              <p className="text-xs text-muted-foreground">
                {w.start_time?.slice(0, 5)}–{w.end_time?.slice(0, 5)} · {t('position')} {w.position}
              </p>
            </div>
            {w.status === "offered" ? (
              <Badge>{t('offered')}</Badge>
            ) : (
              <Badge variant="secondary">{t('inQueue')}</Badge>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive text-xs"
              onClick={() => leaveWaitlistMutation.mutate(w.id)}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  // Upcoming bookings content (shared between tabs)
  const upcomingContent = (
    <>
      {isLoading ? (
        [1, 2].map((i) => <Skeleton key={i} className="h-24 w-full" />)
      ) : upcoming?.length ? (
        <>
          {/* Desktop: table view */}
          <div className="hidden lg:block">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-muted-foreground border-b">
                  <th className="pb-3 font-medium">{t('date')}</th>
                  <th className="pb-3 font-medium">{t('time')}</th>
                  <th className="pb-3 font-medium">{t('court')}</th>
                  <th className="pb-3 font-medium">{t('status')}</th>
                  <th className="pb-3 font-medium">{t('pinCode')}</th>
                  <th className="pb-3 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {upcoming.map((b: BookingWithCourt) => {
                  const bp = (payments as BookingPaymentRow[]).filter((p: BookingPaymentRow) => p.booking_id === b.id);
                  const up = bp.find((p: BookingPaymentRow) => p.user_id === user?.id);
                  // Sibling-badge fix: a paid group sibling has no own payment row
                  // (the payment hangs on the primary via booking_group_id) — fall
                  // back to the booking's own payment_status, not hardcoded "unpaid".
                  const ps = up?.status ?? (bp.length > 0 ? bp[0].status : (b.payment_status ?? "unpaid"));
                  return (
                    <tr key={b.id} className="cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => setSelectedBookingId(b.id)}>
                      <td className="py-3 text-sm font-medium">{format(new Date(b.start_time), "EEE d MMM", { locale: sv })}</td>
                      <td className="py-3 text-sm">{format(new Date(b.start_time), "HH:mm")}–{format(new Date(b.end_time), "HH:mm")}</td>
                      <td className="py-3 text-sm">{b.courts?.name}</td>
                      <td className="py-3"><PaymentStatusBadge status={ps} /></td>
                      <td className="py-3 font-mono text-sm font-bold text-primary">{b.pin_code}</td>
                      <td className="py-3">
                        <div className="flex gap-1.5">
                          {isCustomerRescheduleWindow(b) && (
                            canCustomerReschedule(b) ? (
                              <Button variant="outline" size="sm" className="text-xs" onClick={(e) => { e.stopPropagation(); setRescheduleBooking(b); }}>
                                <ArrowRightLeft className="h-3 w-3 mr-1" />{t('reschedule')}
                              </Button>
                            ) : (
                              // C1-D1: MATCHi-mirrored/pushed → admin-only move during coexistence. Disabled + tooltip (don't silently hide).
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span tabIndex={0} onClick={(e) => e.stopPropagation()}>
                                    <Button variant="outline" size="sm" className="text-xs" disabled>
                                      <ArrowRightLeft className="h-3 w-3 mr-1" />{t('reschedule')}
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>{t('rescheduleMatchiBlocked')}</TooltipContent>
                              </Tooltip>
                            )
                          )}
                          {b.status === "confirmed" && canCancel(b) && (
                            <Button variant="outline" size="sm" className="text-xs text-destructive" onClick={(e) => { e.stopPropagation(); setCancelTarget(b); }}>
                              <X className="h-3 w-3 mr-1" />{t('cancelBooking')}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Mobile: card view */}
          <div className="lg:hidden space-y-3">
            {upcoming.map((b: BookingWithCourt) => (
              <BookingCard key={b.id} booking={b} showCancel {...bookingCardProps} />
            ))}
          </div>
        </>
      ) : (
        <p className="text-center text-muted-foreground py-8">{t('noUpcomingBookings')}</p>
      )}
    </>
  );

  // History content
  const historyContent = (
    <>
      {isLoading ? (
        [1, 2].map((i) => <Skeleton key={i} className="h-24 w-full" />)
      ) : past?.length ? (
        <>
          {/* Desktop: sortable table */}
          <div className="hidden lg:block">
            <BookingHistoryTable
              bookings={past}
              payments={payments as BookingPaymentRow[]}
              userId={user?.id}
              onSelect={(id) => setSelectedBookingId(id)}
            />
          </div>
          {/* Mobile: card view */}
          <div className="lg:hidden space-y-3">
            {past.map((b: BookingWithCourt) => (
              <BookingCard key={b.id} booking={b} {...bookingCardProps} />
            ))}
          </div>
        </>
      ) : (
        <p className="text-center text-muted-foreground py-8">{t('noHistory')}</p>
      )}
    </>
  );

  // Calendar tab content (mobile only)
  const calendarContent = (
    <>
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          v.{format(weekStart, "w", { locale: sv })} · {format(weekStart, "d MMM", { locale: sv })} – {format(addWeeks(weekStart, 1), "d MMM yyyy", { locale: sv })}
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setWeekStart((w) => addWeeks(w, -1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setWeekStart(getWeekStart(new Date()))}>
            {t('common:today')}
          </Button>
          <Button variant="outline" size="icon" onClick={() => setWeekStart((w) => addWeeks(w, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {calLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <WeekCalendar
          events={calendarEvents}
          weekStart={weekStart}
          startHour={hallHours?.start ?? 6}
          endHour={hallHours?.end ?? 24}
        />
      )}
    </>
  );

  // Desktop sidebar
  const desktopSidebar = (
    <div className="space-y-4">
      <MiniCalendarWidget
        bookings={(bookings as BookingWithCourt[] | undefined) ?? []}
        onDayClick={(date) => {
          setWeekStart(getWeekStart(date));
        }}
        selectedDate={weekStart}
      />
      <CalendarSyncSection />
      <Card>
        <CardContent className="p-4">
          <p className="text-sm font-medium text-muted-foreground">
            {t('thisMonthStats', { count: monthStats.count, amount: monthStats.amount })}
          </p>
        </CardContent>
      </Card>
    </div>
  );

  // Desktop main content (no tabs, show upcoming + history inline)
  const desktopMain = (
    <div className="space-y-6">
      {waitlistSection}

      {/* Upcoming section */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">{t('common:upcoming')}</h2>
        {upcomingContent}
      </div>

      {/* History section */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">{t('history')}</h2>
        {historyContent}
      </div>
    </div>
  );

  // Mobile content (tabs)
  const mobileContent = (
    <div className="space-y-4 pb-20">
      <h1 className="text-2xl font-bold">{t('myBookings')}</h1>

      {waitlistSection}

      <Tabs defaultValue="upcoming">
        <TabsList className="w-full">
          <TabsTrigger value="upcoming" className="flex-1">{t('common:upcoming')}</TabsTrigger>
          <TabsTrigger value="history" className="flex-1">{t('history')}</TabsTrigger>
          <TabsTrigger value="calendar" className="flex-1">{t('calendar')}</TabsTrigger>
        </TabsList>
        <TabsContent value="upcoming" className="space-y-3 mt-3">
          {upcomingContent}
        </TabsContent>
        <TabsContent value="history" className="space-y-3 mt-3">
          {historyContent}
        </TabsContent>
        <TabsContent value="calendar" className="mt-3 space-y-3">
          {calendarContent}
        </TabsContent>
      </Tabs>
    </div>
  );

  return (
    <>
      {/* Desktop layout */}
      <div className="hidden lg:block space-y-4 pb-20">
        <h1 className="text-2xl font-bold">{t('myBookings')}</h1>
        <ResponsiveLayout
          main={desktopMain}
          sidebar={desktopSidebar}
          sidebarPosition="right"
          sidebarWidth="w-80"
        />
      </div>

      {/* Mobile layout */}
      <div className="lg:hidden">
        {mobileContent}
      </div>

      {selectedBookingId && (
        <BookingDetailModal
          open={!!selectedBookingId}
          onOpenChange={(open) => { if (!open) setSelectedBookingId(null); }}
          bookingId={selectedBookingId}
        />
      )}

      {rescheduleBooking && hallId && (
        <RescheduleDialog
          open={!!rescheduleBooking}
          onClose={() => setRescheduleBooking(null)}
          booking={rescheduleBooking}
          hallId={hallId}
        />
      )}

      <CancelBookingDialog
        open={!!cancelTarget}
        onOpenChange={(open) => { if (!open) setCancelTarget(null); }}
        onConfirm={() => {
          if (cancelTarget) {
            cancelMutation.mutate(cancelTarget.id, { onSuccess: () => setCancelTarget(null) });
          }
        }}
        courtName={cancelTarget?.courts?.name ?? "Bana"}
        date={cancelTarget ? format(new Date(cancelTarget.start_time), "EEE d MMM", { locale: sv }) : ""}
        time={cancelTarget ? `${format(new Date(cancelTarget.start_time), "HH:mm")}–${format(new Date(cancelTarget.end_time), "HH:mm")}` : ""}
        isPaid={isCancelTargetPaid}
        isPending={cancelMutation.isPending}
      />
    </>
  );
}
